import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { env } from '@config/env';

/** Compare dotted numeric versions (e.g. "2026.0701.1"). Returns -1 | 0 | 1. */
function compareVersions(a: string, b: string): number {
  const pa = a.split('.').map((n) => parseInt(n, 10) || 0);
  const pb = b.split('.').map((n) => parseInt(n, 10) || 0);
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const x = pa[i] ?? 0;
    const y = pb[i] ?? 0;
    if (x !== y) return x < y ? -1 : 1;
  }
  return 0;
}

interface OtaManifest {
  version: string | null;
  url?: string;
  checksum?: string | null;
  /** The signed bundle's session key. Shells built with a public key install nothing without it. */
  session_key?: string;
  min_native_version?: string;
}

const JUST_UPDATED_KEY = 'ota_just_updated_to';
/** A bundle that failed to boot and was rolled back; it is not fetched again until a newer one is published. */
const FAILED_VERSION_KEY = 'ota_failed_version';

let checking = false;
/** The bundle version already staged for the next launch; it must not be fetched again on every resume. */
let stagedVersion: string | null = null;

// ---- minimal, framework-free UI (runs at bootstrap, before React mounts) ----

function toast(text: string): void {
  const el = document.createElement('div');
  el.setAttribute('style', [
    'position:fixed', 'left:50%', 'bottom:32px', 'transform:translateX(-50%)', 'z-index:2147483647',
    'background:#111827', 'color:#fff', 'padding:10px 16px', 'border-radius:9999px',
    'font:600 0.8125rem ui-sans-serif,system-ui,sans-serif', 'box-shadow:0 6px 20px rgba(0,0,0,0.3)', 'opacity:0',
    'transition:opacity 0.25s ease',
  ].join(';'));
  el.textContent = text;
  document.body.appendChild(el);
  requestAnimationFrame(() => { el.style.opacity = '1'; });
  setTimeout(() => { el.style.opacity = '0'; setTimeout(() => el.remove(), 300); }, 3200);
}

/** Tappable "Update ready" pill: the member chooses when to reload, so nothing reloads mid-task. */
function showReloadPrompt(bundleId: string, version: string): void {
  const el = document.createElement('div');
  el.setAttribute('role', 'button');
  el.setAttribute('style', [
    'position:fixed', 'left:50%', 'top:calc(env(safe-area-inset-top) + 12px)', 'transform:translateX(-50%)',
    'z-index:2147483647', 'display:flex', 'align-items:center', 'gap:12px', 'white-space:nowrap',
    'background:#111827', 'color:#fff', 'padding:10px 12px 10px 16px', 'border-radius:9999px',
    'font:600 0.8125rem ui-sans-serif,system-ui,sans-serif', 'box-shadow:0 6px 20px rgba(0,0,0,0.3)',
  ].join(';'));
  el.innerHTML = '<span>Update ready. Tap to reload</span><span data-close style="opacity:0.7;padding:0 4px">✕</span>';

  // 1. ✕ hides it; the update still goes live at the next cold start.
  // 2. Anywhere else reloads into the new bundle now.
  el.addEventListener('click', (e) => {
    el.remove();
    if ((e.target as HTMLElement).hasAttribute('data-close')) return;
    localStorage.setItem(JUST_UPDATED_KEY, version);
    void CapacitorUpdater.set({ id: bundleId });
  });
  document.body.appendChild(el);
}

// ---- core check / apply ----

async function checkAndApply(): Promise<void> {
  if (checking) return;
  checking = true;
  try {
    const platform = Capacitor.getPlatform(); // 'ios' | 'android'
    const manifestUrl =
      `${env.coreApiUrl}/public/ota/manifest` +
      `?app_template_id=${encodeURIComponent(env.appTemplateId)}&platform=${encodeURIComponent(platform)}`;

    const res = await fetch(manifestUrl, { headers: { Accept: 'application/json' } });
    if (!res.ok) return;
    const { data: manifest } = (await res.json()) as { data: OtaManifest };
    // Nothing published — or no checksum, which a bundle must never be applied without.
    if (!manifest.version || !manifest.url || !manifest.checksum) return;

    const info = await App.getInfo(); // { version (marketing), build (CURRENT_PROJECT_VERSION) }
    const nativeBuild = info.build || info.version || '0.0.0';
    const minNative = manifest.min_native_version || '0.0.0';
    if (compareVersions(nativeBuild, minNative) < 0) {
      console.info(`[ota] bundle ${manifest.version} needs native >= ${minNative} (have ${nativeBuild}) — skipping`);
      return;
    }

    const current = await CapacitorUpdater.current();
    if (current?.bundle?.version === manifest.version || stagedVersion === manifest.version) return;
    if (localStorage.getItem(FAILED_VERSION_KEY) === manifest.version) return;

    // Download in the background and stage it for the next cold start. `next()` alone applies it
    // the next time the app is backgrounded, and leaving for a payment's browser tab counts, so the
    // bundle would reload under a member mid-payment: wait for the app to be killed instead, and
    // offer a reload prompt meanwhile, since phones rarely kill a backgrounded app. A shell
    // built with the signing public key decrypts with the session key and verifies the checksum's
    // signature, refusing any bundle the private key did not sign.
    const bundle = await CapacitorUpdater.download({
      url: manifest.url,
      version: manifest.version,
      checksum: manifest.checksum,
      sessionKey: manifest.session_key,
    });
    await CapacitorUpdater.next({ id: bundle.id });
    await CapacitorUpdater.setMultiDelay({ delayConditions: [{ kind: 'kill' }] });
    stagedVersion = manifest.version;
    showReloadPrompt(bundle.id, manifest.version);
  } finally {
    checking = false;
  }
}

/**
 * Self-hosted live web-bundle OTA (@capgo/capacitor-updater). NATIVE ONLY.
 *
 * On launch + on every foreground (resume) it polls core-api for the latest bundle for
 * THIS app's `app_template_id` + platform; if newer than the installed bundle AND the
 * native binary satisfies `min_native_version`, it downloads it (checksum required) and
 * stages it, then offers an "Update ready" reload prompt; otherwise the next cold start switches to it. notifyAppReady() on boot confirms the
 * bundle is healthy so CapGo won't auto-roll-back. Non-blocking: boot does not wait on it, and a failed
 * check (offline, say) leaves the installed bundle in place.
 *
 * Native-plugin/permission changes still ship through the store; `min_native_version`
 * keeps a web bundle off any binary too old to run it.
 */
export async function initLiveUpdates(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  if (!env.appTemplateId) return; // OTA channel is keyed by the app's template

  await CapacitorUpdater.notifyAppReady();
  const [next, current, failed] = await Promise.all([
    CapacitorUpdater.getNextBundle(),
    CapacitorUpdater.current(),
    CapacitorUpdater.getFailedUpdate(),
  ]);

  // 1. A bundle that never reached notifyAppReady was rolled back: remember it, so it is not
  //    downloaded and switched to again on every cold start.
  if (failed) localStorage.setItem(FAILED_VERSION_KEY, failed.bundle.version);

  // 2. A bundle staged on an earlier run goes live now, at a cold start, before the member is
  //    mid-task. The updater never does this itself: a relaunch clears the kill delay but boots
  //    the current bundle and waits for the next backgrounding, by which time this run had
  //    re-staged the bundle and re-armed the delay, so it never went live. Only a 'pending' bundle
  //    (downloaded, never run) counts: one already applied stays queued as `next`.
  if (next?.status === 'pending' && next.version !== current.bundle.version) {
    localStorage.setItem(JUST_UPDATED_KEY, next.version);
    await CapacitorUpdater.set({ id: next.id }); // reloads into the new bundle; nothing after this runs
    return;
  }

  // 3. Confirm a switch visibly, only when the bundle running now is the one switched to (a
  //    rolled-back bundle leaves the marker behind on the old one).
  const applied = localStorage.getItem(JUST_UPDATED_KEY);
  localStorage.removeItem(JUST_UPDATED_KEY);
  if (applied && applied === current.bundle.version) {
    setTimeout(() => toast(`✅ Updated to ${applied}`), 600);
  }

  // 4. Re-check whenever the app returns to the foreground, so a freshly published bundle is
  //    downloaded and staged without waiting for the next cold start to look for it.
  void App.addListener('resume', () => { void checkAndApply(); });

  await checkAndApply();
}
